import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';

import { PER_PAGE, fetchFansPage, type FantasticFansError } from './fantasticFans.api';
import type {
  FantasticFanRowData,
  FantasticFansCursor,
  FantasticFansFilter,
  FantasticFansPage,
} from './types';

const POLL_MS = 10_000;

/** Cap on requests per poll cycle — the interval stretches once more pages are loaded. */
const REQUEST_BUDGET_PER_POLL = 3;

/** Stable reference so an empty board doesn't re-trigger framer-motion layout. */
const EMPTY: FantasticFansPage = [];

type FantasticFansQueryKey = readonly ['fans', string, string | undefined, FantasticFansFilter];

/**
 * Dedupes fans by id (keeping the higher count), sorts by `count DESC, name ASC`,
 * and recomputes each bar's width against the global max. The server sends a
 * per-page `relativePercentage`, which is meaningless once pages are concatenated.
 */
export const mergeFans = (rows: FantasticFanRowData[]): FantasticFanRowData[] => {
  const byId = new Map<string, FantasticFanRowData>();
  rows.forEach((row) => {
    const current = byId.get(row.id);
    if (!current || row.count >= current.count) byId.set(row.id, row);
  });

  // `Array.from` rather than spread — the ui tsconfig targets es5.
  const sorted = Array.from(byId.values()).sort((a, b) => (
    b.count !== a.count
      ? b.count - a.count
      : (a.commenterName || '').localeCompare(b.commenterName || '')
  ));

  const globalMax = sorted[0]?.count || 0;
  return sorted.map((fan) => ({
    ...fan,
    relativePercentage: globalMax > 0 ? Math.round((fan.count / globalMax) * 100) : 0,
  }));
};

interface UseFantasticFansArgs {
  streamId?: string;
  featureId?: string;
  filter: FantasticFansFilter;
}

export const useFantasticFans = ({ streamId, featureId, filter }: UseFantasticFansArgs) => {
  const isAllFilter = filter === 'all';
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery<
    FantasticFansPage,
    FantasticFansError,
    InfiniteData<FantasticFansPage, FantasticFansCursor | null>,
    FantasticFansQueryKey,
    FantasticFansCursor | null
  >({
    // `featureId` in the key is what resets the board when the presenter switches
    // interactions — a different key is a different cache entry.
    queryKey: ['fans', streamId ?? '', featureId, filter],
    // Non-null is safe: the query is gated behind `enabled` below.
    queryFn: ({ pageParam }) => fetchFansPage(streamId!, filter, pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (!isAllFilter) return undefined;
      if (lastPage.length < PER_PAGE) return undefined;
      const tail = lastPage[lastPage.length - 1];
      return { afterCount: tail.count, afterName: tail.commenterName };
    },
    // react-query refetches every loaded page sequentially, so a flat interval
    // costs more the further the presenter has scrolled. Stretch it to hold the
    // per-cycle request count roughly constant.
    refetchInterval: (q) => {
      const loaded = q.state.data?.pages.length ?? 1;
      return Math.max(1, Math.ceil(loaded / REQUEST_BUDGET_PER_POLL)) * POLL_MS;
    },
    refetchOnWindowFocus: true,
    // The poll is already the retry loop.
    retry: 1,
    // Hard reset on feature change — don't restore a previously scrolled board.
    gcTime: 0,
    enabled: Boolean(streamId) && Boolean(featureId),
  });

  const { data, hasNextPage } = query;

  const fans = useMemo(() => {
    const pages = data?.pages;
    if (!pages?.length) return EMPTY;
    // Numeric filters are a single unpaginated top-N page: keep the server's
    // ordering and its percentages verbatim.
    return isAllFilter ? mergeFans(pages.flat()) : pages[0];
  }, [data, isAllFilter]);

  // The observer closure only re-runs on `hasNextPage`, so read the live query
  // through a ref rather than capturing stale flags.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    // An IntersectionObserver works regardless of which ancestor actually
    // scrolls (the page here, the modal body under TrackInteractionsModal) —
    // unlike a scroll listener bound to a specific element.
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      const current = queryRef.current;
      if (!current.hasNextPage || current.isFetching || current.isFetchingNextPage) return;
      // `cancelRefetch` defaults to true, which would let a re-firing observer
      // cancel and restart the page it just asked for.
      void current.fetchNextPage({ cancelRefetch: false });
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage]);

  return {
    fans,
    sentinelRef,
    // `isLoading` rather than `isPending`: the latter stays true while the query
    // is disabled, which would pin the loader.
    isLoading: query.isLoading,
    hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  } as const;
};
